/**
 * Breakout's wire shapes (ACP-D47, GFX-D103; docs/gfx-breakout/plan.md, docs/gfx-breakout/relay.md): the relay's
 * WebSocket envelope and the session records its REST routes answer (`breakoutRoutes.ts`).
 *
 * **The envelope is generic** (Q2, Q15): one writer (the session's owner, "the designer") publishes an ordered stream
 * of opaque `payload`s; the relay fans each out to the session's read-only subscribers ("remotes") and never looks
 * inside a payload — the mirror payloads are gfx's (`gfx/packages/sdk/src/player/mirror.ts`). Nothing here names
 * the product: the relay core (`@acp/relay`, an edgekit candidate, D44) and this file move together.
 *
 * **The stream** (Q3, Q4 as decided: the relay stores no stream state):
 *   - `publish {seq}`: an ordered message. `seq` is gap-free per session, stamped by the designer (1, 2, 3, …);
 *     never dropped by the relay. A remote that sees a gap waits briefly for the missing one (API Gateway invokes
 *     the relay concurrently, so two frames can overtake each other), then asks for a `resync`.
 *   - `latest {key, after}`: a coalescable message (a drag preview, a scrub): only the newest per `key` matters. It
 *     has no `seq` of its own; `after` is the `seq` of the ordered message it follows. The relay forwards at most
 *     30 per second per session, the newest winning. `after` greater than a remote's last applied `seq` means it
 *     is ahead of a gap; one relative to the document (a preview) is applied only when `after` equals the last
 *     applied `seq`, and one that is not (a scrub's time) whenever `after` is not ahead — the payload's owner says
 *     which (docs/gfx-breakout/relay.md). A gesture's final state goes as a `publish`, so it is never coalesced.
 *   - `snapshot {seq, to}`: the whole state as of `seq`, to one remote (`to`, answering its `resync`) or to all
 *     (`to: null`, e.g. another scene opened). A remote that applies it continues from `seq + 1`.
 *   - `resync`: a remote asks for a snapshot; the relay forwards it to the designer, or answers `designer {away}`
 *     when no designer is attached (the remote waits and asks again when `designer {present}` arrives).
 *
 * **Joining** (Q5–Q7 as decided: the capability link only, no pairing code): a remote sends `join {token}` with the
 * link's token (from the URL fragment) and is answered `welcome` with its own `credential`, which it keeps to
 * `resume` after a reload or API Gateway's 2 h connection limit. The designer sends `attach {sessionId, token}` with
 * its user token. Both tokens are `<id>.<secret>`; the relay stores only SHA-256 hashes of the secrets.
 *
 * **Clock sync** (Q9): `ping {id, clientTime}` → `pong {id, clientTime, serverTime}`; NTP's clock filter (the
 * lowest-RTT sample of a window) is the client's. Every time on this wire is epoch milliseconds in the relay's
 * clock, except `clientTime` (the client's own, echoed).
 *
 * Client frames are read strictly by the relay; a client reads the relay's frames tolerantly (D35), and every
 * `openEnum` here has an `'unknown'` a reader handles.
 */
import { z } from 'zod';
import { openEnum } from './openEnum.js';
import { entityIdWireSchema, epochMsWireSchema, isoTimestampWireSchema } from './primitives.js';

/** The largest frame the relay accepts or sends: API Gateway WebSocket's 128 KB. A payload must fit inside it. */
export const BREAKOUT_MAX_FRAME_BYTES = 128 * 1024;
/** Longest session label. */
export const BREAKOUT_MAX_LABEL = 120;
/** Longest user token in `attach` (a Cognito ID token is ~1–2 KB). */
const MAX_USER_TOKEN = 8192;

/** A session or remote id: the relay's own (a ULID in the cloud). */
export const breakoutIdWireSchema = entityIdWireSchema;

/**
 * A link token or remote credential as a client sends it: `<id>.<secret>` (base64url). Read loosely here — the relay
 * parses the two halves itself (`@acp/relay/tokens.js`) and answers a malformed one exactly as a wrong one.
 */
export const breakoutSecretTokenWireSchema = z.string().min(1).max(256);

/** A coalescing key: which kind of `latest` message replaces which (`preview`, `transport`, …). */
export const breakoutLatestKeyWireSchema = z.string().regex(/^[a-z][a-z0-9-]{0,31}$/, 'a key is 1-32 lowercase letters, digits or hyphens');

/** The designer's position in the stream: gap-free from 1. */
const seqSchema = z.number().int().positive();

/**
 * An opaque payload: the relay checks only that it is JSON and fits, and never looks inside (Q2). The designer's
 * stream carries gfx's mirror messages (`gfx/packages/sdk/src/player/mirror.ts`); a remote's `report` and `resync`
 * carry its `MirrorReport` and `MirrorResync`.
 */
export const breakoutPayloadWireSchema = z.json();
export type BreakoutPayloadWire = z.infer<typeof breakoutPayloadWireSchema>;

/**
 * A remote's report: an object of its own (a player's `MirrorReport {state, width, height, aspectClass, fps, shownTime,
 * shownAt, offsetMs, rttMs}`), opaque to the relay like every payload — passed to the designer and kept as the remote's
 * last report.
 */
export const breakoutReportWireSchema = z.record(z.string(), z.unknown());
export type BreakoutReportWire = z.infer<typeof breakoutReportWireSchema>;

/**
 * The most bytes of a remote's own payload (a report, a resync's reason), as JSON: what a remote says is forwarded to
 * the designer and a report is kept, so it stays small. A larger one is refused `too-large`.
 */
export const BREAKOUT_MAX_REMOTE_PAYLOAD_BYTES = 4096;

/** Whether the session's designer is attached: remotes wait for `present` before asking for a snapshot. */
export const BREAKOUT_DESIGNER_STATES = ['present', 'away'] as const;
export const breakoutDesignerStateWireSchema = openEnum(BREAKOUT_DESIGNER_STATES);
export type BreakoutDesignerStateWire = z.infer<typeof breakoutDesignerStateWireSchema>;

/** One remote as its owner sees it. */
export const breakoutRemoteWireSchema = z
  .object({
    remoteId: breakoutIdWireSchema,
    /** Whether a socket of it is open now (a remote that left keeps its credential until the session ends). */
    connected: z.boolean(),
    joinedAt: isoTimestampWireSchema,
    lastSeenAt: isoTimestampWireSchema,
    /** Its last `report` (gfx's `MirrorReport` from a player: size, aspect class, fps, clock offset…), or null before one. */
    report: breakoutReportWireSchema.nullable(),
  })
  .strict();
export type BreakoutRemoteWire = z.infer<typeof breakoutRemoteWireSchema>;

// ─── the WebSocket envelope: client → relay ───

/** A remote: join with the link's token. */
const joinFrame = z.object({ type: z.literal('join'), token: breakoutSecretTokenWireSchema }).strict();
/** A remote: reconnect with the credential its `welcome` gave it. */
const resumeFrame = z.object({ type: z.literal('resume'), credential: breakoutSecretTokenWireSchema }).strict();
/** The designer: attach to its session with its user token (re-checked on every frame it sends). */
const attachFrame = z.object({ type: z.literal('attach'), sessionId: breakoutIdWireSchema, token: z.string().min(1).max(MAX_USER_TOKEN) }).strict();
/** Either side: a clock-sync sample (Q9). `clientTime` is the sender's own clock, echoed in the pong. */
const pingFrame = z.object({ type: z.literal('ping'), id: z.number().int().nonnegative(), clientTime: z.number() }).strict();
/** A remote: what it shows and how well, opaque (Q12: measured, not bounded) — forwarded to the designer, kept for `GET …/remotes`. */
const reportFrame = z.object({ type: z.literal('report'), payload: breakoutReportWireSchema }).strict();
/**
 * A remote: ask the designer for a snapshot (joined, reconnected, saw a gap). `lastSeq`: the last it applied (null:
 * none); `payload`: the remote's own words, forwarded as they are (a player's `MirrorResync {reason}`; null: none).
 */
const resyncFrame = z.object({ type: z.literal('resync'), lastSeq: z.number().int().nonnegative().nullable(), payload: breakoutPayloadWireSchema }).strict();
/** The designer: an ordered message (never dropped). `sentAt`: its own estimate of relay time, for latency. */
const publishFrame = z.object({ type: z.literal('publish'), seq: seqSchema, sentAt: epochMsWireSchema.nullable(), payload: breakoutPayloadWireSchema }).strict();
/** The designer: a coalescable message — the newest per `key` wins; `after` is the `seq` it follows. */
const latestFrame = z
  .object({ type: z.literal('latest'), key: breakoutLatestKeyWireSchema, after: z.number().int().nonnegative(), sentAt: epochMsWireSchema.nullable(), payload: breakoutPayloadWireSchema })
  .strict();
/** The designer: the whole state as of `seq` (0: before any message), to one remote or (`to: null`) to all. */
const snapshotFrame = z
  .object({ type: z.literal('snapshot'), seq: z.number().int().nonnegative(), to: breakoutIdWireSchema.nullable(), sentAt: epochMsWireSchema.nullable(), payload: breakoutPayloadWireSchema })
  .strict();

export const breakoutClientFrameWireSchema = z.discriminatedUnion('type', [joinFrame, resumeFrame, attachFrame, pingFrame, reportFrame, resyncFrame, publishFrame, latestFrame, snapshotFrame]);
export type BreakoutClientFrameWire = z.infer<typeof breakoutClientFrameWireSchema>;

/** The frames only a joined remote sends. */
export const BREAKOUT_REMOTE_FRAMES = ['ping', 'report', 'resync'] as const;
/** The frames only an attached designer sends. */
export const BREAKOUT_DESIGNER_FRAMES = ['ping', 'publish', 'latest', 'snapshot'] as const;

// ─── the WebSocket envelope: relay → client ───

/** Why a remote was let go. `disconnected`: its owner disconnected it. */
const revokedReasonSchema = openEnum(['disconnected']);
/** Why a session ended for this socket. `replaced`: the designer attached again from another socket. */
const closedReasonSchema = openEnum(['closed', 'expired', 'replaced']);
/** Why a remote left (to the designer). */
const leftReasonSchema = openEnum(['gone', 'disconnected', 'closed']);

export const BREAKOUT_ERROR_CODES = [
  /** Not a frame of this envelope, or not one this socket may send. */
  'bad-frame',
  /** Over BREAKOUT_MAX_FRAME_BYTES. */
  'too-large',
  /** A wrong, revoked or expired token or credential; a user token that does not verify. */
  'unauthorized',
  /** The user may not break out (`graphics.breakout` in ACP), or does not own the session. */
  'forbidden',
  /** No such session (or remote) — also for one closed or expired since. */
  'not-found',
  /** The session is full (its remotes cap). */
  'session-full',
  /** The owner stopped admitting new screens ("no new screens"). */
  'not-admitting',
  /** Too many frames from this socket. */
  'rate-limited',
  /** The socket has not joined, resumed or attached yet. */
  'not-joined',
  /** A server fault. */
  'internal',
] as const;
export const breakoutErrorCodeWireSchema = openEnum(BREAKOUT_ERROR_CODES);
export type BreakoutErrorCodeWire = z.infer<typeof breakoutErrorCodeWireSchema>;

const welcomeFrame = z
  .object({
    type: z.literal('welcome'),
    sessionId: breakoutIdWireSchema,
    remoteId: breakoutIdWireSchema,
    /** The remote's own credential, on `join` only (null on `resume`): keep it (sessionStorage) to resume. */
    credential: z.string().nullable(),
    serverTime: epochMsWireSchema,
    /** When the session ends (at most 12 h after it opened). */
    expiresAt: epochMsWireSchema,
    designer: breakoutDesignerStateWireSchema,
  })
  .strict();
const attachedFrame = z
  .object({ type: z.literal('attached'), sessionId: breakoutIdWireSchema, serverTime: epochMsWireSchema, expiresAt: epochMsWireSchema, remotes: z.array(breakoutRemoteWireSchema) })
  .strict();
const pongFrame = z.object({ type: z.literal('pong'), id: z.number().int().nonnegative(), clientTime: z.number(), serverTime: epochMsWireSchema }).strict();
const designerFrame = z.object({ type: z.literal('designer'), state: breakoutDesignerStateWireSchema }).strict();
const publishOut = z.object({ type: z.literal('publish'), seq: seqSchema, sentAt: epochMsWireSchema.nullable(), relayedAt: epochMsWireSchema, payload: breakoutPayloadWireSchema }).strict();
const latestOut = z
  .object({ type: z.literal('latest'), key: breakoutLatestKeyWireSchema, after: z.number().int().nonnegative(), sentAt: epochMsWireSchema.nullable(), relayedAt: epochMsWireSchema, payload: breakoutPayloadWireSchema })
  .strict();
const snapshotOut = z
  .object({ type: z.literal('snapshot'), seq: z.number().int().nonnegative(), sentAt: epochMsWireSchema.nullable(), relayedAt: epochMsWireSchema, payload: breakoutPayloadWireSchema })
  .strict();
const resyncOut = z.object({ type: z.literal('resync'), remoteId: breakoutIdWireSchema, lastSeq: z.number().int().nonnegative().nullable(), payload: breakoutPayloadWireSchema }).strict();
const remoteJoined = z.object({ type: z.literal('remote.joined'), remote: breakoutRemoteWireSchema }).strict();
const remoteLeft = z.object({ type: z.literal('remote.left'), remoteId: breakoutIdWireSchema, reason: leftReasonSchema }).strict();
const remoteReport = z.object({ type: z.literal('remote.report'), remoteId: breakoutIdWireSchema, payload: breakoutReportWireSchema }).strict();
const revokedFrame = z.object({ type: z.literal('revoked'), reason: revokedReasonSchema }).strict();
const closedFrame = z.object({ type: z.literal('closed'), reason: closedReasonSchema }).strict();
const errorFrame = z.object({ type: z.literal('error'), code: breakoutErrorCodeWireSchema, message: z.string() }).strict();

export const breakoutServerFrameWireSchema = z.discriminatedUnion('type', [
  welcomeFrame,
  attachedFrame,
  pongFrame,
  designerFrame,
  publishOut,
  latestOut,
  snapshotOut,
  resyncOut,
  remoteJoined,
  remoteLeft,
  remoteReport,
  revokedFrame,
  closedFrame,
  errorFrame,
]);
export type BreakoutServerFrameWire = z.infer<typeof breakoutServerFrameWireSchema>;

// ─── the REST shapes (breakoutRoutes.ts) ───

/** A breakout session as its owner sees it. */
export const breakoutSessionWireSchema = z
  .object({
    sessionId: breakoutIdWireSchema,
    label: z.string(),
    /** The project it mirrors, when the designer named one (informational). */
    projectId: entityIdWireSchema.nullable(),
    createdAt: isoTimestampWireSchema,
    expiresAt: isoTimestampWireSchema,
    /** False after "no new screens": no link admits anyone until a new link is made. */
    admitting: z.boolean(),
    designer: breakoutDesignerStateWireSchema,
    /** Remotes with an open socket now. */
    connectedRemotes: z.number().int().nonnegative(),
    /** Where clients connect: the relay's WebSocket URL. */
    relayUrl: z.string(),
  })
  .strict();
export type BreakoutSessionWire = z.infer<typeof breakoutSessionWireSchema>;

/** A capability link. The token is answered once, when it is made; the relay keeps only its hash. */
export const breakoutLinkWireSchema = z
  .object({
    /** `<id>.<secret>`: goes in the player URL's fragment, never in a query string. */
    token: z.string(),
    /** The player URL with the token in its fragment; null where the stack has no player host. */
    url: z.string().nullable(),
    expiresAt: isoTimestampWireSchema,
  })
  .strict();
export type BreakoutLinkWire = z.infer<typeof breakoutLinkWireSchema>;

export const breakoutSessionPathWireSchema = z.object({ sessionId: breakoutIdWireSchema }).strict();
export const breakoutRemotePathWireSchema = z.object({ sessionId: breakoutIdWireSchema, remoteId: breakoutIdWireSchema }).strict();

export const openBreakoutSessionRequestWireSchema = z
  .object({
    label: z.string().trim().min(1).max(BREAKOUT_MAX_LABEL),
    projectId: entityIdWireSchema.nullable().optional(),
  })
  .strict();
export type OpenBreakoutSessionRequestWire = z.infer<typeof openBreakoutSessionRequestWireSchema>;

export const openBreakoutSessionResponseWireSchema = z.object({ session: breakoutSessionWireSchema, link: breakoutLinkWireSchema }).strict();
export type OpenBreakoutSessionResponseWire = z.infer<typeof openBreakoutSessionResponseWireSchema>;

export const listBreakoutSessionsResponseWireSchema = z.object({ sessions: z.array(breakoutSessionWireSchema) }).strict();
export type ListBreakoutSessionsResponseWire = z.infer<typeof listBreakoutSessionsResponseWireSchema>;

export const breakoutSessionDetailWireSchema = z.object({ session: breakoutSessionWireSchema, remotes: z.array(breakoutRemoteWireSchema) }).strict();
export type BreakoutSessionDetailWire = z.infer<typeof breakoutSessionDetailWireSchema>;

/** `admit: true` makes a new link (the old token stops working); `admit: false` is "no new screens". */
export const setBreakoutLinkRequestWireSchema = z.object({ admit: z.boolean() }).strict();
export type SetBreakoutLinkRequestWire = z.infer<typeof setBreakoutLinkRequestWireSchema>;

export const setBreakoutLinkResponseWireSchema = z.object({ session: breakoutSessionWireSchema, link: breakoutLinkWireSchema.nullable() }).strict();
export type SetBreakoutLinkResponseWire = z.infer<typeof setBreakoutLinkResponseWireSchema>;

export const listBreakoutRemotesResponseWireSchema = z.object({ remotes: z.array(breakoutRemoteWireSchema) }).strict();
export type ListBreakoutRemotesResponseWire = z.infer<typeof listBreakoutRemotesResponseWireSchema>;

export const disconnectBreakoutRemoteResponseWireSchema = z.object({ sessionId: breakoutIdWireSchema, remoteId: breakoutIdWireSchema }).strict();
export type DisconnectBreakoutRemoteResponseWire = z.infer<typeof disconnectBreakoutRemoteResponseWireSchema>;

export const closeBreakoutSessionResponseWireSchema = z.object({ sessionId: breakoutIdWireSchema }).strict();
export type CloseBreakoutSessionResponseWire = z.infer<typeof closeBreakoutSessionResponseWireSchema>;
